import React from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { Card, CardContent } from '@/shared/ui/Card'
import { Button, Input, Radio, Switch, PasswordWithConfirm } from '@/shared/ui'
import { Checkbox } from '@/shared/ui/Checkbox'
import { Modal } from '@/shared/ui/Modal'
import { useToast } from '@/shared/ui/Toast'
import CreditCardPreview from '../components/CreditCardPreview'
import { getLogo } from '@/modules/branding/brandingService'
import { getPlanDiscountPercent } from '@/modules/plans/utils/pricing'

import CheckoutDuplicateSubscriptionAlert from '../components/CheckoutDuplicateSubscriptionAlert'
import StripePayment from '../components/StripePayment'
import { initSubscriptionCheckout, initTrialSetupIntent, startRewardsTrialSubscription } from '../services/checkoutService'
import { isMasterByEmail, normalizeRole } from '@/shared/utils/role'
import { computePaidOk } from '@/modules/auth/utils/subscriptionGate'
import { getPlanByRef, listCheckoutPlansForUser, syncSubscriptionStatus } from '@/modules/plans/services/memberPlanService'
import type { Plan } from '@/modules/plans/types'
import CheckoutPersonalForm from '../components/CheckoutPersonalForm'
import { getTermsText } from '@/modules/settings/services/settingsService'
import { updateProfile } from '@/modules/profile/services/profileService'
import { validateCoupon, type CouponValidation } from '@/modules/discount-coupons/services/couponsService'
import AffiliateInfoCard from '../components/AffiliateInfoCard'
import CheckoutSummaryCard from '../components/CheckoutSummaryCard'
import { useTrialStatus } from '@/modules/plans/hooks/useTrialStatus'
import { getSelectedPlan } from '@/modules/checkout/utils/selection'
import { getMyRewardsTrialAttempt } from '@/modules/rewards/services/rewardsTrialService'
import { openStripeBillingPortal } from '@/modules/billing/services/billingService'

function formatPrice(amountCents: number, currency?: string) {
  const value = (amountCents || 0) / 100
  const code = (currency || 'BRL').toUpperCase()
  try { return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: code }).format(value) } catch { return 'R$ ' + value.toFixed(2) }
}

type DuplicateSubscriptionInfo = {
  subscription_id?: string | null
  status?: string | null
  current_period_end?: string | null
  trial_ends_at?: string | null
  is_trial?: boolean | null
  period_valid?: boolean | null
  message?: string | null
  email?: string | null
}

export default function CheckoutPage() {
  const { user, profile } = useAuth() as any
  const { add: toast } = useToast()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const trialStatus = useTrialStatus()
  const [rewardsTrialAttempt, setRewardsTrialAttempt] = React.useState<any>(null)

  React.useEffect(() => {
    let cancelled = false
    if (!user?.id) {
      setRewardsTrialAttempt(null)
      return
    }
    ;(async () => {
      try {
        const a = await getMyRewardsTrialAttempt(user.id)
        if (!cancelled) setRewardsTrialAttempt(a)
      } catch {
        if (!cancelled) setRewardsTrialAttempt(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [user?.id])

  let selectedPlan: { plan: string; interval: 'month'|'year' } | null = null
  try {
    selectedPlan = getSelectedPlan()
  } catch {
    selectedPlan = null
  }

  const planFromQuery = (params.get('plan') || '').trim()
  const trialParam = (params.get('trial') || '').trim().toLowerCase()
  const hasTrialFlag = trialParam === '1' || trialParam === '7d'
  const planRefRaw = (planFromQuery || (selectedPlan?.plan || '')).trim()
  const planRef = (planRefRaw || (hasTrialFlag ? 'trial_7d' : '')).trim()
  const isFreemiumPlanRef = planRef.toLowerCase() === 'trial_7d'
  const rawInterval = (params.get('interval') || '').trim().toLowerCase()
  const intervalFromQuery = rawInterval === 'year' ? 'year' : rawInterval === 'month' ? 'month' : null
  const interval = (intervalFromQuery || selectedPlan?.interval || 'month') as 'month'|'year'
  const rewardsTrialParam = (params.get('rewards_trial') || params.get('rewards') || '').trim().toLowerCase()
  const isRewardsTrialSelectionFromQuery =
    rewardsTrialParam === '1' || rewardsTrialParam === 'true' || rewardsTrialParam === '30d'
  const hasActiveRewardsTrial = rewardsTrialAttempt?.status === 'active'
  const hasActiveTrialProfile = !!user?.id && trialStatus.inTrial && !isFreemiumPlanRef
  type TrialKind = 'rewards_30d' | null
  const trialKind: TrialKind = isRewardsTrialSelectionFromQuery ? 'rewards_30d' : null
  type CheckoutMode = 'paid' | 'trial_setup' | 'trial_active'
  const checkoutMode: CheckoutMode =
    trialKind
      ? (hasActiveRewardsTrial ? 'trial_active' : 'trial_setup')
      : (hasActiveTrialProfile ? 'trial_active' : 'paid')
  const isTrialSetupMode = checkoutMode === 'trial_setup'
  const isTrialActiveMode = checkoutMode === 'trial_active'
  const isTrialUiMode = checkoutMode !== 'paid'

  const [publishableKey, setPublishableKey] = React.useState<string | null>(null)
  const [clientSecret, setClientSecret] = React.useState<string | null>(null)
  const [subscriptionId, setSubscriptionId] = React.useState<string | null>(null)
  const [initializing, setInitializing] = React.useState(false)
  const [trialSetupStatus, setTrialSetupStatus] = React.useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const [error, setError] = React.useState<string | null>(null)
  const [details, setDetails] = React.useState<{ name: string; email: string; phone: string; couponCode?: string; referralCode?: string; password: string; confirmPassword: string; nameOnCard?: string; expiry?: string }>(() => ({
    name: '', email: '', phone: '',
    couponCode: (params.get('cupom') || params.get('coupon') || '').trim() || undefined,
    referralCode: (params.get('ref') || params.get('referral') || '').trim() || undefined,
    password: '', confirmPassword: '', nameOnCard: '', expiry: '',
  }))
  const [couponPreview, setCouponPreview] = React.useState<CouponValidation | null>(null)
  const [acceptedTerms, setAcceptedTerms] = React.useState(false)
  const [affiliate, setAffiliate] = React.useState<{ code: string|null; name: string|null; email: string|null; commission: string|null } | null>(null)
  const [openTerms, setOpenTerms] = React.useState(false)
  const [animateName, setAnimateName] = React.useState(false)
  const [openSuccess, setOpenSuccess] = React.useState(false)
  const [emailExistsModal, setEmailExistsModal] = React.useState(false)
  const [paymentError, setPaymentError] = React.useState<string | null>(null)
  const [processingPay, setProcessingPay] = React.useState(false)
  const [step, setStep] = React.useState<1|2>((!!(user?.id) ? 2 : 1) as 1|2)
  const payRef = React.useRef<null | (() => Promise<void>)>(null)
  const [logoUrl, setLogoUrl] = React.useState<string | null>(null)
  const [plan, setPlan] = React.useState<Plan | null>(null)
  const [plans, setPlans] = React.useState<Plan[]>([])
  const [showPlanSelector, setShowPlanSelector] = React.useState(false)
  const [planIntervalChoice, setPlanIntervalChoice] = React.useState<'month'|'year'>(interval)
  const [selectedPlanRef, setSelectedPlanRef] = React.useState<string>('')
  const [applyingCoupon, setApplyingCoupon] = React.useState(false)
  const [appliedCouponCode, setAppliedCouponCode] = React.useState<string>('')
  const autoApplyRefCouponDoneRef = React.useRef(false)
  const [editingDetails, setEditingDetails] = React.useState(false)
  const [retryTick, setRetryTick] = React.useState(0)
  const stallTimerRef = React.useRef<number | null>(null)
  const initAttemptRef = React.useRef(false)
  const [autoRetryDone, setAutoRetryDone] = React.useState(false)
  const [duplicateInfo, setDuplicateInfo] = React.useState<DuplicateSubscriptionInfo | null>(null)
  const [duplicateSyncing, setDuplicateSyncing] = React.useState(false)
  const duplicateAutoRetryRef = React.useRef<Set<string>>(new Set())
  const handleReturnToSystem = React.useCallback((event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault()
    if (!user?.id) return
    const roleRaw = String(
      profile?.role ||
      (user as any)?.user_metadata?.role ||
      (user as any)?.app_metadata?.role ||
      '',
    ).toLowerCase()
    const role = normalizeRole(roleRaw)
    const dest =
      isMasterByEmail(user?.email) || role === 'master'
        ? '/dashboard-master'
        : role === 'afiliado'
          ? '/dashboard-affiliate'
          : '/oportunidades'
    navigate(dest, { replace: true })
  }, [navigate, profile?.role, user])
  const handleNoPayment = (resp: any) => {
    const noPay = !!(resp?.noPaymentRequired ?? resp?.no_payment_required)
    if (!noPay) return false
    setClientSecret(null)
    setSubscriptionId(resp?.subscriptionId || resp?.subscription_id || null)
    setStep(2)
    navigate('/obrigado', { replace: true })
    return true
  }
  const buildDuplicateInfo = (err: any, emailText: string): DuplicateSubscriptionInfo | null => {
    const raw = (err?.subscription && typeof err.subscription === 'object') ? err.subscription : null
    if (!raw) {
      return {
        message: err?.message || null,
        email: (err?.email || emailText || '').trim() || null,
      }
    }
    return {
      subscription_id: raw.subscription_id || raw.subscriptionId || null,
      status: raw.status || null,
      current_period_end: raw.current_period_end || null,
      trial_ends_at: raw.trial_ends_at || null,
      is_trial: raw.is_trial ?? null,
      period_valid: typeof raw.period_valid === 'boolean' ? raw.period_valid : null,
      message: raw.message || err?.message || null,
      email: (err?.email || emailText || '').trim() || null,
    }
  }
  const isDuplicateError = (err: any): boolean => {
    if (!err) return false
    if (err?.code === 'subscription_already_exists') return true
    const msgRaw = typeof err === 'string' ? err : (err?.message || err?.error || '')
    const msg = String(msgRaw || '').toLowerCase()
    return msg.includes('subscription_already_exists') || msg.includes('assinatura ativa')
  }
  const isDuplicateValid = (info: DuplicateSubscriptionInfo | null): boolean => {
    if (!info) return false
    if (typeof info.period_valid === 'boolean') return info.period_valid
    return computePaidOk(info as any)
  }
  const handleDuplicateCheckoutError = async (
    err: any,
    payload: { plan: string; interval?: 'month'|'year'; referralCode?: string; couponCode?: string; email?: string },
    onSuccess: (resp: any) => void,
    options?: { forceSync?: boolean },
  ): Promise<boolean> => {
    if (!isDuplicateError(err)) return false
    const info = buildDuplicateInfo(err, payload.email || '')
    setDuplicateInfo(info)
    setError(null)
    if (!info) return false
    if (isDuplicateValid(info)) return false
    const subId = info.subscription_id || null
    if (!subId) return false
    const already = duplicateAutoRetryRef.current.has(subId)
    if (!options?.forceSync && already) return false
    if (!options?.forceSync) duplicateAutoRetryRef.current.add(subId)
    setDuplicateSyncing(true)
    try {
      try { await syncSubscriptionStatus(subId) } catch {}
      const resp = await initSubscriptionCheckout(payload)
      onSuccess(resp)
      setDuplicateInfo(null)
      return true
    } catch (e2: any) {
      if (isDuplicateError(e2)) {
        const info2 = buildDuplicateInfo(e2, payload.email || '')
        setDuplicateInfo(info2)
        setError(null)
      }
      return false
    } finally {
      setDuplicateSyncing(false)
    }
  }
  const handleOpenPortal = async () => {
    try {
      const returnUrl = (typeof window !== 'undefined' && window.location?.href) ? window.location.href : undefined
      await openStripeBillingPortal(returnUrl)
    } catch (e: any) {
      toast({ title: 'Stripe', description: e?.message || 'Falha ao abrir o portal', variant: 'error', soft: true })
    }
  }
  React.useEffect(() => { (async () => { try { const isDark = document.documentElement.classList.contains('dark') || window.matchMedia?.('(prefers-color-scheme: dark)')?.matches; const url = await getLogo('home' as any, !isDark); setLogoUrl(url) } catch {} })() }, [])

  // Validate coupon on change (debounced)
  React.useEffect(() => {
    let cancelled = false
    const c = (details.couponCode || '').trim()
    if (!c) { setCouponPreview(null); return }
    const t = setTimeout(() => {
      ;(async () => {
        try {
          const v = await validateCoupon(c)
          if (!cancelled) setCouponPreview(v.ok ? v : { ok: false, reason: v.reason || 'Cupom invalido' })
        } catch { if (!cancelled) setCouponPreview(null) }
      })()
    }, 400)
    return () => { cancelled = true; clearTimeout(t) }
  }, [details.couponCode])

  // Prefill email from query param so the user sees their info
  React.useEffect(() => {
    const emailParam = (params.get('email') || '').trim().toLowerCase()
    if (emailParam && !details.email) setDetails((d) => ({ ...d, email: emailParam }))
  }, [params])

  // Prefill name/phone from query params (non-destructive)
  React.useEffect(() => {
    const qName = (params.get('name') || params.get('full_name') || '').trim()
    const qPhone = (params.get('phone') || '').trim()
    if (qName && !details.name) setDetails((d) => ({ ...d, name: qName }))
    if (qPhone && !details.phone) setDetails((d) => ({ ...d, phone: qPhone }))
  }, [params])

  // Prefill from authenticated session/profile (non-destructive)
  React.useEffect(() => {
    if (user?.email && !details.email) setDetails((d) => ({ ...d, email: user.email! }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email])
  React.useEffect(() => {
    const fullName = (profile?.full_name || '').toString().trim()
    const phone = (profile?.phone || '').toString().trim()
    const patch: any = {}
    if (fullName && !details.name) patch.name = fullName
    if (phone && !details.phone) patch.phone = phone
    if (Object.keys(patch).length) setDetails((d) => ({ ...d, ...patch }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.full_name, profile?.phone])


  // Resolve afiliacao para exibir resumo no checkout
  React.useEffect(() => { (async () => {
    try {
      let code = (details.referralCode || '').trim()
      if (!code) {
        try { const mod = await import('@/modules/affiliates/services/referralService'); const saved = (mod as any)?.getReferralCode?.(); if (saved) code = saved } catch {}
      }
      const sb = (await import('@/lib/supabase')).supabase
      if (!code && user?.id) {
        try {
          const mem = await sb.from('account_users').select('account_id').eq('user_id', user.id).limit(1)
          const accountId = (mem.data as any)?.[0]?.account_id
          if (accountId) {
            const acc = await sb.from('accounts').select('referrer_affiliate_id').eq('id', accountId).maybeSingle()
            const affId = (acc.data as any)?.referrer_affiliate_id
            if (affId) {
              const prof = await sb.from('v_profiles_contact_master').select('full_name,email').eq('id', affId).maybeSingle()
              const set = await sb.from('affiliate_settings').select('commission_mode, commission_value, recurrence').eq('affiliate_user_id', affId).maybeSingle()
              const mode = (set.data as any)?.commission_mode || 'percent'
              const val = Number(((set.data as any)?.commission_value) || 0)
              const comm = (mode === 'percent') ? (Math.round(val*100) + '%') : formatPrice(Math.round(val*100), 'BRL')
              const rec = (String(((set.data as any)?.recurrence)||'one_time') === 'recurring') ? 'recorrente' : 'unica'
              setAffiliate({ code: null, name: (prof.data as any)?.full_name || null, email: (prof.data as any)?.email || null, commission: (comm + ' ' + rec) })
              // Prefill cupom do afiliado se o campo estiver vazio
              try {
                if (!((details.couponCode||'').trim())) {
                  const now = Date.now()
                  const { data: list } = await sb
                    .from('discount_coupons')
                    .select('code, discount_type, percent_off, amount_off_cents, recurrence, start_at, end_at, is_active, created_at')
                    .eq('affiliate_user_id', affId)
                    .order('created_at', { ascending: false })
                    .limit(20)
                  const items = (list||[]).filter((r:any)=> (r?.is_active !== false) && (!r?.start_at || new Date(r.start_at).getTime() <= now) && (!r?.end_at || new Date(r.end_at).getTime() >= now))
                  if (items.length>0) {
                    items.sort((a:any,b:any)=>{ const ar=String(a?.recurrence||'one_time').toLowerCase()==='recurring'?1:0; const br=String(b?.recurrence||'one_time').toLowerCase()==='recurring'?1:0; if(ar!==br) return br-ar; const ap=(a?.discount_type==='percent')?Number(a?.percent_off||0):-1; const bp=(b?.discount_type==='percent')?Number(b?.percent_off||0):-1; if(ap!==bp) return bp-ap; const af=(a?.discount_type==='fixed')?Number(a?.amount_off_cents||0):-1; const bf=(b?.discount_type==='fixed')?Number(b?.amount_off_cents||0):-1; if(af!==bf) return bf-af; const at=new Date(a?.created_at||0).getTime()||0; const bt=new Date(b?.created_at||0).getTime()||0; return bt-at })
                    const pick = items[0]
                    if (pick?.code) setDetails((d)=> ({ ...d, couponCode: String(pick.code) }))
                  } else {
                    // Fallback: affiliate_coupons (legado)
                    try {
                      const { data: list2 } = await sb
                        .from('affiliate_coupons')
                        .select('code, mode, value, active, recurrence, created_at')
                        .eq('affiliate_user_id', affId)
                        .order('created_at', { ascending: false })
                        .limit(20)
                      const items2 = (list2||[]).filter((r:any)=> (r?.active !== false))
                      if (items2.length>0) {
                        items2.sort((a:any,b:any)=>{ const ar=String(a?.recurrence||'one_time').toLowerCase()==='recurring'?1:0; const br=String(b?.recurrence||'one_time').toLowerCase()==='recurring'?1:0; if(ar!==br) return br-ar; const ap=(String(a?.mode||'').toLowerCase()==='percent')?Number((a?.value||0)*100):-1; const bp=(String(b?.mode||'').toLowerCase()==='percent')?Number((b?.value||0)*100):-1; if(ap!==bp) return bp-ap; const af=(String(a?.mode||'').toLowerCase()==='fixed')?Number((a?.value||0)*100):-1; const bf=(String(b?.mode||'').toLowerCase()==='fixed')?Number((b?.value||0)*100):-1; if(af!==bf) return bf-af; const at=new Date(a?.created_at||0).getTime()||0; const bt=new Date(b?.created_at||0).getTime()||0; return bt-at })
                        const pick2 = items2[0]
                        if (pick2?.code) setDetails((d)=> ({ ...d, couponCode: String(pick2.code) }))
                      }
                    } catch {}
                  }
                }
              } catch {}
              return
            }
          }
        } catch {}
      }
      if (!code) { setAffiliate(null); return }
      const aff = await sb.from('affiliates').select('user_id, code, active').ilike('code', code).maybeSingle()
      if (aff.data?.user_id) {
        const prof = await sb.from('v_profiles_contact_master').select('full_name,email').eq('id', aff.data.user_id).maybeSingle()
        const set = await sb.from('affiliate_settings').select('commission_mode, commission_value, recurrence').eq('affiliate_user_id', aff.data.user_id).maybeSingle()
        const mode = (set.data as any)?.commission_mode || 'percent'
        const val = Number(((set.data as any)?.commission_value) || 0)
        const comm = (mode === 'percent') ? (Math.round(val*100) + '%') : formatPrice(Math.round(val*100), 'BRL')
        const rec = (String(((set.data as any)?.recurrence)||'one_time') === 'recurring') ? 'recorrente' : 'unica'
        setAffiliate({ code: code.toUpperCase(), name: (prof.data as any)?.full_name || null, email: (prof.data as any)?.email || null, commission: (comm + ' ' + rec) })
        // Prefill cupom do afiliado se o campo estiver vazio (servi�o centralizado)
        try {
          if (!((details.couponCode||'').trim())) {
            const svc = await import('@/modules/discount-coupons/services/couponsService')
            const best = await (svc as any).getBestCouponCodeForAffiliateReferral(code)
            if (best) setDetails((d)=> ({ ...d, couponCode: String(best) }))
          }
        } catch {}
      } else {
        setAffiliate(null)
      }
    } catch { setAffiliate(null) }
  })() }, [details.referralCode, user?.id])

  // Prefill garantir: se veio ref e o campo de cupom est� vazio, resolve pela Edge Function
  React.useEffect(() => { (async () => {
    try {
      const hasRef = !!(details.referralCode||'').trim()
      const hasCoupon = !!(details.couponCode||'').trim()
      if (hasRef && !hasCoupon) {
        const svc = await import('@/modules/discount-coupons/services/couponsService')
        const best = await (svc as any).getBestCouponCodeForAffiliateReferral((details.referralCode||'').trim())
        if (best) setDetails((d)=> ({ ...d, couponCode: String(best) }))
      }
    } catch {}
  })() }, [details.referralCode, details.couponCode])

  // Auto-aplicar cupom preeenchido do afiliado (valida e aplica no Stripe)
  React.useEffect(() => { (async () => {
    try {
      if (isTrialUiMode) return
      if (isFreemiumPlanRef) return
      if (autoApplyRefCouponDoneRef.current) return
      const typed = ((details.couponCode||'').trim())
      if (!typed) return
      // S� ap�s termos plan/publishable/step 2
      const ready = !!planRef && !!plan && plan.active && (step===2)
      if (!ready) return
      // Valida e aplica
      const { validateCoupon } = await import('@/modules/discount-coupons/services/couponsService')
      const v = await validateCoupon(typed)
      if (!v?.ok) return
      const resp = await initSubscriptionCheckout({
        plan: planRef,
        interval,
        referralCode: (details.referralCode||'')?.trim()||undefined,
        couponCode: typed,
        email: (user?.email || details.email || '').trim() || undefined,
      })
      if ((resp as any)?.publishable && ((resp as any).publishable as any) !== publishableKey) setPublishableKey((resp as any).publishable as any)
      if (handleNoPayment(resp)) {
        setAppliedCouponCode(typed.toUpperCase())
        autoApplyRefCouponDoneRef.current = true
        return
      }
      setClientSecret(resp.clientSecret)
      setSubscriptionId(resp.subscriptionId)
      setAppliedCouponCode(typed.toUpperCase())
      autoApplyRefCouponDoneRef.current = true
    } catch {}
  })() }, [details.couponCode, step, planRef, plan, interval])

  React.useEffect(() => { (async () => {
    try {
      const p = await getPlanByRef(planRef)
      // Apenas aceitar plano visÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â­vel e ativo para checkout pÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Âºblico
      if (p && (isFreemiumPlanRef || p.active)) { setPlan(p) }
      else { setPlan(null); if (planRef && !isFreemiumPlanRef) setShowPlanSelector(true) }
    } catch { setPlan(null) }
  })() }, [planRef, isFreemiumPlanRef])

  React.useEffect(() => { (async () => {
    try {
      const list = await listCheckoutPlansForUser(user?.id || null)
      setPlans(list)
    } catch {}
  })() }, [user?.id])

  // Cobran�a anual: desativar quando n�o houver desconto anual configurado
  const annualAllowed = React.useMemo(() => (
    plans.some((p) => {
      try { return p.active && (p as any).visible && p.interval === 'year' && getPlanDiscountPercent(p) > 0 } catch { return false }
    })
  ), [plans])
  React.useEffect(() => {
    if (!annualAllowed && planIntervalChoice === 'year') setPlanIntervalChoice('month')
  }, [annualAllowed])

  // Se nÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â£o houver planRef, abra o seletor de planos automaticamente
  React.useEffect(() => {
    if (!planRef && !isFreemiumPlanRef) {
      setShowPlanSelector(true)
      return
    }
    if (isTrialSetupMode && isFreemiumPlanRef) {
      setShowPlanSelector(true)
    }
  }, [planRef, isFreemiumPlanRef, isTrialSetupMode])
  // Ajusta etapa inicial quando o estado de autenticaÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â§ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â£o muda
  React.useEffect(() => { if (!clientSecret) setStep(user?.id ? 2 : 1) }, [user?.id])

  // SetupIntent para Trial Rewards: criado apenas apos o usuario
  // escolher o plano que sera cobrado e estar no passo 2.
  React.useEffect(() => {
    if (!isTrialSetupMode) return
    if (clientSecret) return
    if (step !== 2) return
    const planId = (plan?.id || plan?.slug || '').toString().toLowerCase()
    if (!planId || planId === 'trial_7d') return
    if (trialSetupStatus === 'loading' || trialSetupStatus === 'ready') return
    ;(async () => {
      try {
        setTrialSetupStatus('loading')
        const resp = await initTrialSetupIntent({
          email: (user?.email || details.email || '').trim() || undefined,
          referralCode: (details.referralCode || '').trim() || undefined,
          name: (details.name || '').trim() || undefined,
          phone: (details.phone || '').trim() || undefined,
        })
        if (resp.publishable && resp.publishable !== publishableKey) {
          setPublishableKey(resp.publishable)
        }
        setClientSecret(resp.clientSecret)
        setTrialSetupStatus('ready')
      } catch (e: any) {
        const msg = (e?.message || '').toString()
        if (!msg || msg === 'Failed to fetch') {
          setError('Falha ao preparar coleta do cartao. Verifique sua conexao com a internet e tente novamente.')
        } else {
          setError(msg)
        }
        setTrialSetupStatus('error')
      }
    })()
  }, [isTrialSetupMode, publishableKey, clientSecret, step, plan?.id, plan?.slug, user?.email, details.email, details.name, details.phone, details.referralCode, trialSetupStatus])

  // Inicializa checkout uma vez (somente autenticado)
  React.useEffect(() => {
    if (isTrialUiMode) return
    if ((!publishableKey && !isFreemiumPlanRef) || !planRef || clientSecret) return
    if (initAttemptRef.current) return
    if (step !== 2) return
    if (!user?.id) return
    // Exigir plano permitido (ativo+visÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â­vel)
    if (!plan) return; if (!isFreemiumPlanRef && !plan.active) return
    (async () => {
      const payload0 = {
        plan: planRef,
        interval,
        referralCode: (details.referralCode || '').trim() || undefined,
        couponCode: ((details.couponCode||'').trim().toUpperCase() === (appliedCouponCode||'')) ? (details.couponCode||'').trim() : undefined,
        email: (user?.email || details.email || '').trim() || undefined,
      }
      const applyResp0 = (resp0: any) => {
        if ((resp0 as any)?.publishable && ((resp0 as any).publishable as any) !== publishableKey) {
          setPublishableKey((resp0 as any).publishable as any)
        }
        setDuplicateInfo(null)
        setError(null)
        if (handleNoPayment(resp0)) return
        setClientSecret(resp0.clientSecret)
        setSubscriptionId(resp0.subscriptionId)
        setStep(2)
        const autoCode0 = (resp0 as any)?.applied_coupon_code
        if (autoCode0 && !((details.couponCode||'').trim())) {
          setDetails((d)=> ({ ...d, couponCode: String(autoCode0) }))
          setAppliedCouponCode(String(autoCode0||'').toUpperCase())
        } else {
          setAppliedCouponCode(((details.couponCode||'').trim().toUpperCase()))
        }
      }
      try {
        initAttemptRef.current = true
        setInitializing(true)
        const resp0 = await initSubscriptionCheckout(payload0)
        applyResp0(resp0)
      } catch (e:any) {
        const recovered = await handleDuplicateCheckoutError(e, payload0, applyResp0)
        if (recovered || isDuplicateError(e)) return
        setDuplicateInfo(null)
        setError(e?.message || "Falha ao iniciar o checkout")
      } finally {
        setInitializing(false)
        initAttemptRef.current = false
      }
    })()
  }, [user?.id, publishableKey, planRef, interval, clientSecret, details.referralCode, step, retryTick])

  // Guarda contra travas: se apÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â³s 12s nÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â£o houver clientSecret, mostra erro e permite tentar novamente
  React.useEffect(() => {
    const shouldWatch = isTrialUiMode
      ? false
      : (isFreemiumPlanRef
          ? (!!user?.id && step === 2 && !!planRef && !!plan && !clientSecret && !error && !initializing)
          : (!!publishableKey && !!user?.id && step === 2 && !!planRef && !!plan && plan.active && !clientSecret && !error && !initializing))
    if (!shouldWatch) { if (stallTimerRef.current) { window.clearTimeout(stallTimerRef.current as any); stallTimerRef.current = null } ; return }
    if (stallTimerRef.current) return
    stallTimerRef.current = window.setTimeout(() => {
      stallTimerRef.current = null
      // 1� evento: tenta um retry silencioso
      if (!autoRetryDone) {
        setAutoRetryDone(true)
        setRetryTick((v)=> v+1)
        return
      }
      // 2� evento: exibe a mensagem e habilita o bot�o
      initAttemptRef.current = false
      setInitializing(false)
      setError('Nao foi possivel iniciar o checkout agora. Toque em "Tentar novamente" ou volte e selecione o plano outra vez.')
    }, 12000) as any
    return () => { if (stallTimerRef.current) { window.clearTimeout(stallTimerRef.current as any); stallTimerRef.current = null } }
  }, [publishableKey, user?.id, step, planRef, plan, clientSecret, error, initializing, autoRetryDone, isTrialUiMode])
  // Reinit defensivo: ao entrar no passo 2 logado e com plano valido, dispara init automaticamente
  React.useEffect(() => {
    const ready = step === 2 && !!plan && (isFreemiumPlanRef || plan.active) && !clientSecret && !initializing && !error
    if (!ready || initAttemptRef.current) return
    initAttemptRef.current = true
    ;(async () => { try { await retryInitNow() } finally { initAttemptRef.current = false } })()
  }, [step, plan?.id, clientSecret, initializing, error])
  async function afterPaid() {
    try {
      if (isTrialSetupMode) {
        const effectivePlanRef = (plan?.id ? String(plan.id).trim() : '')
        if (!plan || !effectivePlanRef || effectivePlanRef.toLowerCase() === 'trial_7d') {
          setError('Selecione um plano para ser cobrado apos o Trial Rewards.')
          return
        }
        try {
          await startRewardsTrialSubscription({
            plan: effectivePlanRef,
            interval,
            referralCode: (details.referralCode || '').trim() || undefined,
            email: (user?.email || details.email || '').trim() || undefined,
            couponCode: (appliedCouponCode || '').trim() || undefined,
          } as any)
        } catch (e: any) {
          const msg =
            e?.message ||
            'Falha ao ativar o Trial Rewards com cartao salvo. Tente novamente.'
          setError(msg)
          return
        }
      } else if (subscriptionId) {
        // Mantem comportamento legado: sincroniza assinatura apos pagamento concluido
        ;(async () => {
          try {
            const mod = await import('@/lib/supabase')
            await mod.supabase.functions.invoke('sync-subscription-status', {
              body: { subscription_id: subscriptionId },
            })
          } catch {}
        })()
      }
    } catch {}
    navigate('/obrigado', { replace: true })
  }
  async function retryInitNow(forceDuplicateSync?: boolean) {
    const emailToUse = (user?.email || details.email || '').trim() || undefined
    const payload = {
      plan: planRef,
      interval,
      referralCode: (details.referralCode || '').trim() || undefined,
      couponCode: (((details.couponCode||'').trim().toUpperCase() === (appliedCouponCode||'')) ? (details.couponCode||'').trim() : undefined),
      email: emailToUse,
    }
    const applyResp = (resp: any) => {
      if ((resp as any)?.publishable && ((resp as any).publishable as any) !== publishableKey) {
        setPublishableKey((resp as any).publishable as any)
      }
      setDuplicateInfo(null)
      setError(null)
      if (handleNoPayment(resp)) return
      setClientSecret(resp.clientSecret)
      setSubscriptionId(resp.subscriptionId)
      setAppliedCouponCode(((details.couponCode||'').trim().toUpperCase()))
      setStep(2)
    }
    try {
      if (checkoutMode !== 'paid') {
        // Para o fluxo de Trial Rewards, o SetupIntent e iniciado automaticamente.
        // O botao de retry nao reinicia o fluxo de cobranca.
        return
      }
      setError(null)
      initAttemptRef.current = true
      setInitializing(true)
      const resp = await initSubscriptionCheckout(payload)
      applyResp(resp)
    } catch (e:any) {
      const recovered = await handleDuplicateCheckoutError(e, payload, applyResp, { forceSync: !!forceDuplicateSync })
      if (recovered || isDuplicateError(e)) return
      setDuplicateInfo(null)
      setError(e?.message || 'Falha ao iniciar o checkout')
    } finally {
      setInitializing(false)
      initAttemptRef.current = false
    }
  }
  async function retryInitTrialNow() {
    if (!isTrialSetupMode) return
    setError(null)
    setClientSecret(null)
    setTrialSetupStatus('idle')
  }
  const emailToUse = (details.email || user?.email || '').trim()
  const missingRequired = !(details.name || '').trim() || !emailToUse
  const planIdLower = (plan?.id || '').toString().toLowerCase()
  const trialPeriodLabel = trialKind === 'rewards_30d' ? '30 dias' : 'periodo de trial'
  const requirePlanForPayment = !plan || (isTrialSetupMode && (!planIdLower || planIdLower === 'trial_7d'))

  return (
    <div className="mx-auto w-full max-w-[900px] pb-12">
      <div className="mt-[50px] mb-[50px] flex items-center justify-center">{!!logoUrl && (<img src={logoUrl} alt="logo" className="h-14 w-auto" />)}</div>
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-semibold">Finalizar assinatura</h1>
        <p className="text-sm text-foreground/70">
          {isTrialSetupMode
            ? 'Ative seu Trial Rewards salvando um cartao de credito. O trial tem duracao de 30 dias, com validacao por volume minimo nos primeiros 7 dias; se nao cumprir, o acesso pode ser cancelado e voce sera direcionado para contratar um plano.'
            : (isTrialActiveMode
                ? (trialKind === 'rewards_30d' || rewardsTrialAttempt?.status === 'active'
                    ? 'Voce esta em um Trial Rewards. Nenhum valor sera cobrado durante o periodo de teste; apos esse prazo, a cobranca do seu plano sera feita automaticamente, se voce nao cancelar.'
                    : `Voce esta em um periodo promocional sem cobranca.${trialStatus.daysLeft > 0 ? ` Restam aproximadamente ${trialStatus.daysLeft} dia(s) de acesso.` : ''}`)
                : (isFreemiumPlanRef
                    ? 'Crie sua conta para ativar o plano Freemium (acesso limitado, sem cobranca).'
                    : 'Crie sua conta e realize o pagamento com seguranca via Stripe.'))}
        </p>
        {user?.id && (
          <div className="mt-4 flex justify-center">
            <Button variant="outline" type="button" onClick={handleReturnToSystem}>
              Voltar ao sistema
            </Button>
          </div>
        )}
      </div>

      {rewardsTrialAttempt?.status === 'failed' && (
        <div className="mb-4 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-foreground">
          <div className="font-medium">Seu Trial Rewards foi encerrado.</div>
          <div className="mt-1 text-foreground/80">
            Para continuar com acesso, selecione um plano e conclua a contrata��o no checkout.
          </div>
        </div>
      )}
      {duplicateInfo && (
        <CheckoutDuplicateSubscriptionAlert
          info={duplicateInfo}
          email={(duplicateInfo?.email || (user?.email || details.email || ''))}
          syncing={duplicateSyncing}
          onRetry={() => retryInitNow(true)}
          onOpenPortal={handleOpenPortal}
          onGoPlans={() => navigate('/my-plans')}
        />
      )}
      {error && !duplicateInfo && (
        <div className="mb-4 flex items-center justify-between gap-4 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          <div>{error}</div>
          {(step===2) && (
            <button
              type="button"
              className="rounded-md border border-destructive/40 bg-background px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
              onClick={() => {
                if (isTrialSetupMode) {
                  void retryInitTrialNow()
                  return
                }
                void retryInitNow()
              }}
            >Tentar novamente</button>
            )}
        </div>
      )}

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="step1" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
            <div className="mx-auto max-w-xl space-y-5">
              <CheckoutPersonalForm
                value={{ name: details.name, email: details.email, password: details.password, confirmPassword: details.confirmPassword, phone: details.phone, referralCode: details.referralCode }}
                onChange={(patch)=> setDetails({ ...details, ...patch })}
              />
              <div className="flex justify-end">
                <Button onClick={async ()=> {
                  try {
                    setError(null)
                    const email=(details.email||'').trim().toLowerCase(); if (!/.+@.+\..+/.test(email)) throw new Error('Informe um e-mail valido')
                    if ((details.password||'').length<8) throw new Error('A senha deve ter pelo menos 8 caracteres')
                    if (details.password!==details.confirmPassword) throw new Error('As senhas nao conferem')
                    const phoneValue = (details.phone||'').trim()
                    if (!phoneValue) throw new Error('Informe um telefone valido')
                    const sb=(await import('@/lib/supabase')).supabase
                    if (isMasterByEmail(email)) { setEmailExistsModal(true); return }
                    try { const { data } = await sb.functions.invoke('check-email-availability', { body: { email } }); const resp = Array.isArray(data) ? (data as any)[0] : (data as any); if (resp?.available === false) { setEmailExistsModal(true); return } } catch {}
                    try {
                      const { data } = await sb.functions.invoke('check-phone-availability', { body: { phone: phoneValue } })
                      const resp = Array.isArray(data) ? (data as any)[0] : (data as any)
                      if (resp?.available === false) {
                        throw new Error('Este numero de telefone ja esta vinculado a outra conta. Faca login ou recupere seu acesso.')
                      }
                    } catch (e:any) {
                      if (e?.message) throw e
                    }
                    const su = await sb.auth.signUp({ email, password: details.password, options: { data: { role: 'member', full_name: details.name || undefined, phone: phoneValue || undefined } } }); if (su.error) throw su.error
                    // Autentica e, se falhar por confirmaÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â§ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â£o, garante confirmaÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â§ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â£o pelo admin e tenta novamente
                    let signed = false
                    try { const r = await sb.auth.signInWithPassword({ email, password: details.password }); if (!r.error) signed = true } catch {}
                    try { await sb.functions.invoke('admin-ensure-customer', { body: { email, name: details.name || undefined, phone: phoneValue || undefined } }) } catch {}
                    if (!signed) { try { await sb.auth.signInWithPassword({ email, password: details.password }) } catch {} }
                    if (isTrialSetupMode) {
                      setStep(2)
                      return
                    }
                    const r = await initSubscriptionCheckout({
                      plan: planRef,
                      interval,
                      referralCode: (details.referralCode || '').trim() || undefined,
                      couponCode:
                        (details.couponCode || '').trim().toUpperCase() === (appliedCouponCode || '')
                          ? (details.couponCode || '').trim()
                          : undefined,
                      email: (user?.email || email || details.email || '').trim() || undefined,
                    })
                    setDuplicateInfo(null)
                    setError(null)
                    if (handleNoPayment(r)) return
                    setClientSecret(r.clientSecret)
                    setSubscriptionId(r.subscriptionId)
                    setStep(2)
                    {
                      const autoCode1 = (r as any)?.applied_coupon_code
                      if (autoCode1 && !(details.couponCode || '').trim()) {
                        setDetails((d) => ({ ...d, couponCode: String(autoCode1) }))
                        setAppliedCouponCode(String(autoCode1 || '').toUpperCase())
                      }
                    }
                  } catch (e:any) {
                    const emailDup = (user?.email || details.email || '').trim()
                    const payloadDup = {
                      plan: planRef,
                      interval,
                      referralCode: (details.referralCode || '').trim() || undefined,
                      couponCode:
                        (details.couponCode || '').trim().toUpperCase() === (appliedCouponCode || '')
                          ? (details.couponCode || '').trim()
                          : undefined,
                      email: (user?.email || emailDup || details.email || '').trim() || undefined,
                    }
                    const applyDup = (resp: any) => {
                      if (handleNoPayment(resp)) return
                      setDuplicateInfo(null)
                      setError(null)
                      setClientSecret(resp.clientSecret)
                      setSubscriptionId(resp.subscriptionId)
                      setStep(2)
                      const autoCode1 = (resp as any)?.applied_coupon_code
                      if (autoCode1 && !(details.couponCode || '').trim()) {
                        setDetails((d) => ({ ...d, couponCode: String(autoCode1) }))
                        setAppliedCouponCode(String(autoCode1 || '').toUpperCase())
                      }
                    }
                    const recovered = await handleDuplicateCheckoutError(e, payloadDup, applyDup, { forceSync: true })
                    if (recovered || isDuplicateError(e)) { setStep(2); return }
                    const msg = (e?.message||'').toString().toLowerCase()
                    if ((msg.includes('already') && (msg.includes('registered') || msg.includes('exists'))) || (msg.includes('constraint') && msg.includes('users_email'))) { setEmailExistsModal(true); return }
                    setDuplicateInfo(null)
                    setError(e?.message||'Falha ao criar conta')
                  }
                }}>Cadastrar</Button>
              </div>
            </div>
          </motion.div>
        )}
        {step === 2 && (
          <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="order-1 md:order-1 space-y-4">
                <div className="rounded-xl border border-border p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm font-medium">Seus dados</div>
                    <button type="button" className="text-xs text-primary underline" onClick={()=> setEditingDetails((v)=> !v)}>{editingDetails ? 'fechar' : 'editar'}</button>
                  </div>
                  {!editingDetails ? (
                    <>
                      <div className="text-sm text-foreground/80 space-y-1">
                        <div><span className="text-foreground/60">Nome:</span> {details.name || "-"}</div>
                        <div><span className="text-foreground/60">E-mail:</span> {emailToUse || "-"}</div>
                        <div><span className="text-foreground/60">Telefone:</span> {details.phone || "-"}</div>
                      </div>
                      {missingRequired && (
                        <div className="mt-2 text-xs text-amber-400">
                          Complete nome e e-mail em "editar" para finalizar o pagamento.
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="space-y-2">
                      <div className="space-y-1.5">
                        <label className="text-sm text-foreground/80">Nome completo</label>
                        <Input value={details.name} onChange={(e)=> setDetails({ ...details, name: e.target.value })} placeholder="Seu nome" />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm text-foreground/80">E-mail</label>
                        <Input type="email" value={details.email || user?.email || ''} onChange={(e)=> setDetails({ ...details, email: e.target.value })} placeholder="seu@email.com" />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm text-foreground/80">Telefone</label>
                        <Input value={details.phone} onChange={(e)=> setDetails({ ...details, phone: e.target.value })} placeholder="(11) 99999-9999" />
                      </div>
                      <div className="space-y-1">
                        <PasswordWithConfirm
                          password={details.password}
                          confirm={details.confirmPassword}
                          onChange={(p)=> setDetails({ ...details, password: p.password ?? details.password, confirmPassword: p.confirm ?? details.confirmPassword })}
                          labels={{ password: 'Nova senha', confirm: 'Confirmar nova senha' }}
                          placeholders={{ password: '(opcional)', confirm: '(opcional)' }}
                          columns={1}
                        />
                      </div>
                      <div className="flex gap-2 pt-1">
                        <Button variant="outline" onClick={()=> setEditingDetails(false)}>Cancelar</Button>
                        <Button onClick={async ()=> {
                          try {
                            const email=(details.email||user?.email||'').trim().toLowerCase(); if (!/.+@.+\..+/.test(email)) throw new Error('Informe um e-mail valido')
                            if (details.password && details.password.length<8) throw new Error('A senha deve ter pelo menos 8 caracteres')
                            // Atualiza conta se logado
                            const sb=(await import('@/lib/supabase')).supabase
                            if (user?.id) {
                              const patch: any = {}
                              if (email && email!==user.email) patch.email = email
                              if (details.password) patch.password = details.password
                              if (Object.keys(patch).length>0) {
                                const upd = await sb.auth.updateUser(patch)
                                if (upd.error) throw upd.error
                              }
                              try { await updateProfile(user.id, { full_name: details.name || null, phone: details.phone || null }) } catch {}
                            }
                            setEditingDetails(false)
                            toast({ title: 'Dados atualizados', variant: 'success', soft: true })
                          } catch (e:any) { toast({ title: 'Falha ao atualizar', description: e?.message||String(e), variant: 'error', soft: true }) }
                        }}>Salvar</Button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="rounded-xl border border-border p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm font-medium">Plano selecionado</div>
                    <button type="button" className="text-xs text-primary underline" onClick={()=> { setShowPlanSelector((v)=> !v); setSelectedPlanRef((plan?.id || plan?.slug || '')); setPlanIntervalChoice(interval) }}>{showPlanSelector ? 'fechar' : 'alterar'}</button>
                  </div>
                  {!showPlanSelector ? (
                    plan ? (
                      <div className="text-sm text-foreground/80 space-y-1">
                        <div className="font-semibold text-foreground">{plan.name}</div>
                        {(() => {
                          const base = plan.amount_cents
                          const planPct = getPlanDiscountPercent(plan, interval)
                          const afterPlan = planPct > 0 ? Math.max(0, Math.floor(base * (100 - planPct) / 100)) : base
                          let final = afterPlan
                          const lines: string[] = []
                          const labelInterval = plan.interval === 'year' ? 'ano' : 'mes'
                          if (planPct > 0 && afterPlan < base) {
                            lines.push(`Desconto do plano (${planPct}%): -${formatPrice(base - afterPlan, plan.currency)}`)
                          }
                          const typed = ((details.couponCode||'').trim().toUpperCase())
                          const c = (appliedCouponCode && typed === appliedCouponCode && (couponPreview?.ok)) ? couponPreview : null
                          if (c?.ok) {
                            if (c.type === 'percent' && typeof c.percent_off === 'number') {
                              const f = Math.max(0, Math.floor(afterPlan * (100 - Math.floor(c.percent_off)) / 100))
                              lines.push(`Cupom (${Math.floor(c.percent_off)}%): -${formatPrice(afterPlan - f, plan.currency)}`)
                              final = f
                            } else if (c.type === 'fixed' && typeof c.amount_off_cents === 'number') {
                              const f = Math.max(0, afterPlan - Math.floor(c.amount_off_cents))
                              lines.push(`Cupom: -${formatPrice(afterPlan - f, plan.currency)}`)
                              final = f
                            }
                          }
                          if (isTrialUiMode) {
                            return (
                              <div className="space-y-0.5">
                                <div className="text-xs text-foreground/60 line-through">
                                  {formatPrice(final, plan.currency)} / {labelInterval}
                                </div>
                                <div className="font-medium text-emerald-400">
                                  R$ 0,00 / {trialPeriodLabel}
                                </div>
                              </div>
                            )
                          }
                          return (
                            <div className="space-y-0.5">
                              {final !== base ? (
                                <div className="space-y-0.5">
                                  <div className="text-xs text-foreground/60 line-through">{formatPrice(base, plan.currency)} / {labelInterval}</div>
                                  <div className="font-medium">{formatPrice(final, plan.currency)} / {labelInterval}</div>
                                  {lines.map((t, i) => (<div key={i} className="text-xs text-foreground/70">{t}</div>))}
                                </div>
                              ) : (
                                <div>{formatPrice(base, plan.currency)} / {labelInterval}</div>
                              )}
                            </div>
                          )
                        })()}
                        {plan.description && <div className="text-xs text-foreground/60">{plan.description}</div>}
                        {isTrialUiMode && (
                          <div className="mt-2 rounded-md border border-amber-500/60 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-100">
                            Durante o Trial Rewards, nenhum valor ser� cobrado no seu cart�o para este plano. Ap�s o per�odo de teste, a cobran�a recorrente ser� feita automaticamente com base neste plano, se voc� n�o cancelar antes.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-foreground/60">Carregando plano...</div>
                    )
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-foreground/70">Mensal</div>
                        <Switch disabled={!annualAllowed} checked={planIntervalChoice==='year'} onChange={(e:any)=> setPlanIntervalChoice(e?.target?.checked ? 'year' : 'month')} />
                        <div className="text-xs text-foreground/70">Anual</div>
                      </div>
                      <div className="space-y-2">
                        {plans.filter(p => {
                          if (!p.active) return false
                          // Filtra por intervalo apenas para planos mensais/anuais;
                          // planos day/week aparecem sempre, desde que ativos.
                          if ((p.interval === 'month' || p.interval === 'year') && p.interval !== planIntervalChoice) return false
                          const id = (p.id || p.slug || '').toString().toLowerCase()
                          if (id === 'trial_7d') return false
                          return true
                        }).map((p) => (
                          <label key={p.id} className={`flex w-full items-center justify-between rounded-lg border p-3 ${ (selectedPlanRef===((p.id||p.slug))) ? 'border-primary' : 'border-border' }`}>
                            <div className="space-y-0.5">
                              <div className="text-sm font-medium text-foreground">{p.name}</div>
                              {(() => {
                                const base = p.amount_cents
                                const pct = getPlanDiscountPercent(p)
                                const after = pct > 0 ? Math.max(0, Math.floor(base * (100 - pct) / 100)) : base
                                const labelInterval = p.interval === 'year' ? 'ano' : 'mes'
                                if (isTrialUiMode) {
                                  return (
                                    <div className="space-y-0.5">
                                      <div className="text-xs text-foreground/60 line-through">
                                        {formatPrice(after, p.currency)} / {labelInterval}
                                      </div>
                                      <div className="text-sm font-semibold text-emerald-400">
                                        R$ 0,00 / {trialPeriodLabel}
                                      </div>
                                    </div>
                                  )
                                }
                                if (after < base) {
                                  return (
                                    <div className="space-y-0.5">
                                      <div className="text-xs text-foreground/60 line-through">{formatPrice(base, p.currency)} / {labelInterval}</div>
                                      <div className="text-xs font-medium">{formatPrice(after, p.currency)} / {labelInterval}</div>
                                    </div>
                                  )
                                }
                                return (<div className="text-xs text-foreground/70">{formatPrice(base, p.currency)} / {labelInterval}</div>)
                              })()}
                            </div>
                            <Radio name="plan" checked={selectedPlanRef===((p.id||p.slug))} onChange={()=> setSelectedPlanRef(p.id || p.slug)} />
                          </label>
                        ))}
                        {plans.filter(p => {
                          if (!p.active) return false
                          if ((p.interval === 'month' || p.interval === 'year') && p.interval !== planIntervalChoice) return false
                          const id = (p.id || p.slug || '').toString().toLowerCase()
                          if (id === 'trial_7d') return false
                          return true
                        }).length===0 && (
                          <div className="text-sm text-foreground/60">Nenhum plano {planIntervalChoice==='year' ? 'anual' : 'mensal'} disponivel.</div>
                        )}
                      </div>
                      {isTrialUiMode && (
                        <div className="mt-2 rounded-md border border-amber-500/60 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
                          Durante o Trial Rewards, nenhum valor ser� cobrado no seu cart�o, independentemente do plano selecionado. Ap�s o per�odo de teste, a cobran�a recorrente ser� feita automaticamente com base no plano escolhido, se voc� n�o cancelar antes.
                        </div>
                      )}
                      <div className="flex gap-2 pt-1">
                        <Button variant="outline" onClick={()=> setShowPlanSelector(false)}>Cancelar</Button>
                        <Button disabled={!selectedPlanRef} onClick={async ()=> {
                          if (!selectedPlanRef) return
                          // Atualiza URL e reinicia checkout
                          const newPlan = selectedPlanRef
                          const newInterval = planIntervalChoice
                          setShowPlanSelector(false)
                          if (checkoutMode === 'paid') {
                            setClientSecret(null); setSubscriptionId(null)
                          }
                          // Atualiza search params sem desmontar o componente
                          const searchParts: string[] = [
                            `plan=${encodeURIComponent(newPlan)}`,
                            `interval=${encodeURIComponent(newInterval)}`,
                          ]
                          if (isTrialSetupMode) {
                            searchParts.push('rewards_trial=1')
                          }
                          navigate({ pathname: '/checkout', search: `?${searchParts.join('&')}` }, { replace: true })
                          if (isTrialSetupMode) {
                            // No fluxo de Trial Rewards, o SetupIntent nao depende do plano selecionado.
                            // Apenas atualizamos o plano que sera cobrado apos o Trial Rewards.
                            setAppliedCouponCode(((details.couponCode||'').trim().toUpperCase()))
                            return
                          }
                          try {
                            setInitializing(true)
                            const resp1 = await initSubscriptionCheckout({ plan: newPlan, interval: newInterval, referralCode: (details.referralCode||'')?.trim()||undefined, couponCode: ((details.couponCode||'').trim().toUpperCase() === (appliedCouponCode||'')) ? (details.couponCode||'').trim() : undefined, email: (user?.email || details.email || '').trim() || undefined })
                            if ((resp1 as any)?.publishable && ((resp1 as any).publishable as any) !== publishableKey) setPublishableKey((resp1 as any).publishable as any)
                            setDuplicateInfo(null)
                            setError(null)
                            if (handleNoPayment(resp1)) return
                            setClientSecret(resp1.clientSecret); setSubscriptionId(resp1.subscriptionId);
                            {
                              const autoCode2 = (resp1 as any)?.applied_coupon_code
                              if (autoCode2 && !((details.couponCode||'').trim())) { setDetails((d)=> ({ ...d, couponCode: String(autoCode2) })); setAppliedCouponCode(String(autoCode2||'').toUpperCase()) }
                              else { setAppliedCouponCode(((details.couponCode||'').trim().toUpperCase())) }
                            }
                          } catch (e:any) { setError(e?.message || 'Falha ao iniciar o checkout para o novo plano') } finally { setInitializing(false) }
                        }}>Selecionar plano</Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Cupom no passo 2 (para usuarios logados tambem) */}
                <div className="rounded-xl border border-border p-4 space-y-2">
                  <div className="text-sm font-medium">Cupom de desconto</div>
                  <div className="flex items-center gap-2">
                    <input className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-1 text-sm text-foreground shadow-sm outline-none placeholder:opacity-60 focus:ring-2 focus:ring-ring"
                      value={details.couponCode || ''}
                      onChange={(e)=> setDetails({ ...details, couponCode: e.target.value })}
                      placeholder="Ex.: PROMO10" />
                    <Button
                      variant="outline"
                      disabled={applyingCoupon || ((details.couponCode||'').trim().length===0) || ((details.couponCode||'').trim().toUpperCase() === (appliedCouponCode||''))}
                      onClick={async()=> {
                        try {
                          setApplyingCoupon(true)
                          const code = (details.couponCode||'').trim()
                          if (!code) return
                          if (code) {
                            try {
                              const v = await validateCoupon(code)
                              setCouponPreview(v.ok ? v : { ok:false, reason: v.reason || 'inv�lido' })
                              if (!v.ok) { toast({ title:'Cupom', description: v.reason || 'Cupom invalido', variant:'error', soft:true }); setApplyingCoupon(false); return }
                            } catch { setCouponPreview({ ok:false, reason:'Falha ao validar' }); setApplyingCoupon(false); toast({ title:'Cupom', description:'Falha ao validar cupom', variant:'error', soft:true }); return }
                          }
                          if (isTrialSetupMode) {
                            setAppliedCouponCode(code.toUpperCase())
                            toast({
                              title: 'Cupom durante o Trial Rewards',
                              description: 'O desconto do cupom sera considerado somente nas cobrancas apos o periodo do Trial Rewards. Nenhum valor sera cobrado durante o teste.',
                              soft: true,
                            })
                            return
                          }
                          const resp = await initSubscriptionCheckout({
                            plan: planRef,
                            interval,
                            referralCode: (details.referralCode||'')?.trim()||undefined,
                            couponCode: code || undefined,
                            email: (user?.email || details.email || '').trim() || undefined,
                          })
                          if ((resp as any)?.publishable && ((resp as any).publishable as any) !== publishableKey) setPublishableKey((resp as any).publishable as any)
                          setDuplicateInfo(null)
                          setError(null)
                          if (handleNoPayment(resp)) { setAppliedCouponCode(code.toUpperCase()); return }
                          setClientSecret(resp.clientSecret)
                          setSubscriptionId(resp.subscriptionId)
                          setAppliedCouponCode(code.toUpperCase())
                          toast({ title: 'Cupom', description: 'Aplicado (se eleg�vel).', soft: true })
                        } catch (e:any) {
                          setAppliedCouponCode('')
                          toast({ title: 'Cupom', description: e?.message||String(e), variant: 'error', soft: true })
                        } finally { setApplyingCoupon(false) }
                      }}
                    >{applyingCoupon ? 'Aplicando...' : 'Aplicar'}</Button>
                  </div>
                  {!!(details.couponCode||'').trim() && (
                    <div className="text-xs text-foreground/70">
                      Status: {couponPreview?.ok ? 'valido' : 'invalido'} {(appliedCouponCode && ((details.couponCode||'').trim().toUpperCase() === (appliedCouponCode||''))) ? '(aplicado)' : ''}
                    </div>
                  )}
                </div>

                {(() => { const typed=((details.couponCode||'').trim().toUpperCase()); const applied=(appliedCouponCode && typed===appliedCouponCode && (couponPreview?.ok)); return (
                  <CheckoutSummaryCard
                    plan={plan}
                    coupon={(applied ? couponPreview : null) as any}
                    couponCode={(applied ? (details.couponCode||'') : '')}
                    affiliateLabel={(affiliate ? (affiliate.name || affiliate.email || affiliate.code || null) : null) as any}
                    billingInterval={interval}
                    isTrialMode={isTrialUiMode}
                  />
                ) })()}
                {affiliate && (
                  <AffiliateInfoCard affiliate={affiliate} />
                )}
              </div>
              <div className="order-2 md:order-2 space-y-4">


                <CreditCardPreview name={details.nameOnCard || details.name} expiry={details.expiry || undefined} brand={'visa'} animateName={animateName} />
                <div className="rounded-xl border border-border p-4 space-y-4">
                  {publishableKey && clientSecret ? (
                    <StripePayment
                      publishableKey={publishableKey}
                      clientSecret={clientSecret}
                      onPaid={afterPaid}
                      onError={(m) => setPaymentError(m)}
                      controlRef={payRef}
                      billing={{
                        name: details.nameOnCard || details.name,
                        email: details.email || user?.email || undefined,
                        phone: details.phone || undefined,
                      }}
                      mode={isTrialSetupMode ? 'setup' : 'payment'}
                    />
                  ) : (
                    <Card className={requirePlanForPayment ? 'border-sky-500/30 bg-sky-500/10 text-sky-500 dark:text-sky-400' : ''}>
                      <CardContent className="pt-6 text-sm">
                        {requirePlanForPayment ? (
                          <div className="space-y-1">
                            <div className="text-sm font-medium text-foreground">
                              Selecione um plano para continuar
                            </div>
                            <div className="text-xs text-foreground/70">
                              Escolha um plano na se��o &quot;Plano selecionado&quot; ao lado para liberar os campos de preenchimento do cartao de credito. Assim que o plano for definido, o formulario de pagamento sera carregado aqui.
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-foreground/30 border-t-transparent" />
                            <span>
                              {isTrialSetupMode
                                ? (trialSetupStatus === 'error'
                                  ? 'Nao foi possivel carregar o formulario de pagamento. Verifique sua conexao ou bloqueadores e clique em "Tentar novamente".'
                                  : 'Preparando pagamento para salvar seu cartao...')
                                : 'Preparando pagamento...'}
                            </span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                  {!requirePlanForPayment && (
                    <div className="space-y-1.5">
                      <label className="text-sm text-foreground/80">Nome impresso no cartao</label>
                      <input
                        className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-1 text-sm text-foreground shadow-sm outline-none placeholder:opacity-60 focus:ring-2 focus:ring-ring"
                        value={details.nameOnCard || ''}
                        onChange={(e)=> { setDetails({ ...details, nameOnCard: e.target.value }); setAnimateName(true); setTimeout(()=> setAnimateName(false), 600) }}
                        placeholder="EX.: JOAO SILVA"
                        autoComplete="cc-name"
                        required
                      />
                    </div>
                  )}
                </div>
                <div className="mt-2 flex items-start gap-2 text-sm">
                  <Checkbox checked={acceptedTerms} onChange={(e:any)=> setAcceptedTerms(!!e.currentTarget.checked)} />
                  <div className="text-foreground/80">Ao selecionar estou ciente e de acordo com os termos de servico prestados pelo site. Para ver os termos <button type="button" className="text-primary underline" onClick={()=> setOpenTerms(true)}>clique aqui</button></div>
                </div>
                <div className="flex justify-start">
                  <Button
                    disabled={!acceptedTerms || !clientSecret || processingPay || missingRequired || (!!(couponPreview?.ok) && ((details.couponCode||'').trim().length>0) && (((details.couponCode||'').trim().toUpperCase()) !== (appliedCouponCode||'')))}
                    onClick={async()=> {
                      if (!payRef.current) return
                      if (missingRequired) {
                        toast({ title: 'Complete seus dados', description: 'Preencha nome e e-mail em "Seus dados" para continuar.', variant: 'warning', soft: true })
                        setEditingDetails(true)
                        return
                      }
                      if (isTrialSetupMode) {
                        const planId = (plan?.id || '').toString().toLowerCase()
                        if (!plan || !planId || planId === 'trial_7d') {
                          toast({
                            title: 'Selecione um plano',
                            description: 'Escolha o plano que sera cobrado apos o Trial Rewards.',
                            variant: 'warning',
                            soft: true,
                          })
                          setShowPlanSelector(true)
                          return
                        }
                      }
                      setProcessingPay(true)
                      try {
                        // Se usuario esqueceu de clicar em Aplicar mas o cupom eh valido, aplica antes de pagar
                        if (checkoutMode === 'paid') {
                          const typed = ((details.couponCode||'').trim().toUpperCase())
                          const notApplied = !!(couponPreview?.ok) && (!appliedCouponCode || (typed !== appliedCouponCode))
                          if (notApplied) {
                            try {
                              const resp = await initSubscriptionCheckout({
                                plan: planRef,
                                interval,
                                referralCode: (details.referralCode||'')?.trim()||undefined,
                                couponCode: (details.couponCode||'')?.trim()||undefined,
                                email: (user?.email || details.email || '').trim() || undefined,
                              })
                              if ((resp as any)?.publishable && ((resp as any).publishable as any) !== publishableKey) setPublishableKey((resp as any).publishable as any)
                              setClientSecret(resp.clientSecret)
                              setSubscriptionId(resp.subscriptionId)
                              setAppliedCouponCode(typed)
                            } catch {}
                          }
                        }
                        await payRef.current()
                      } catch { /* handled inside */ }
                      finally { setProcessingPay(false) }
                    }}
                    className={processingPay ? 'pointer-events-none' : ''}
                  >
                    {processingPay ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-foreground/30 border-t-transparent" />
                                                Processando...
                      </span>
                    ) : 'Assinar'}
                  </Button>
                  {((couponPreview?.ok) && ((details.couponCode||'').trim().length>0) && (((details.couponCode||'').trim().toUpperCase()) !== (appliedCouponCode||''))) && (
                    <div className="ml-3 text-xs text-amber-400">Aplique o cupom para continuar.</div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modais */}
      <Modal open={openTerms} onOpenChange={setOpenTerms}>
        <TermsModalContent onClose={()=> setOpenTerms(false)} />
      </Modal>

      <Modal open={openSuccess} onOpenChange={setOpenSuccess}>
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Pagamento confirmado</h2>
          <p className="text-sm text-foreground/70">Compra aprovada. Voce recebera um e-mail com os detalhes. Clique abaixo para fazer login e acessar a plataforma.</p>
          <div className="flex justify-end gap-2">
            <Button onClick={()=> navigate('/login', { replace: true })}>Ir para login</Button>
          </div>
        </div>
      </Modal>

      <Modal open={!!paymentError} onOpenChange={(v)=> { if (!v) setPaymentError(null) }}>
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Pagamento nao aprovado</h2>
          <p className="text-sm text-foreground/70">{paymentError || 'A compra foi negada. Verifique com seu banco ou a bandeira do cartao para entender o motivo e tente novamente.'}</p>
          <div className="flex justify-end"><Button variant="outline" onClick={()=> setPaymentError(null)}>Fechar</Button></div>
        </div>
      </Modal>

      <Modal open={emailExistsModal} onOpenChange={setEmailExistsModal}>
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">E-mail ja cadastrado</h2>
          <p className="text-sm text-foreground/70">Este e-mail ja esta registrado. Para seguranca, faca login para continuar o pagamento.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={()=> setEmailExistsModal(false)}>Fechar</Button>
            <Button onClick={()=> navigate('/login', { replace: true })}>Ir para login</Button>
          </div>
        </div>
      </Modal>

      {/* Sem modal de conta criada: com fallback por e-mail no init-subscription-checkout */}
    </div>
  )
}

function TermsModalContent({ onClose }: { onClose: () => void }) {
  const [text, setText] = React.useState<string>('')
  const [loading, setLoading] = React.useState(true)
  React.useEffect(() => { (async () => { try { setText(await getTermsText()) } finally { setLoading(false) } })() }, [])
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">Termos de Servico</h2>
      <div className="prose prose-invert max-w-none text-sm text-foreground/80">{loading ? 'Carregando...' : (text || 'Sem termos definidos.')}</div>
      <div className="flex justify-end"><Button onClick={onClose}>Fechar</Button></div>
    </div>
  )
}



